import React, { Component, ErrorInfo, ReactNode } from 'react';
import { RotateCcw, Home, AlertCircle, Copy, Check, ChevronDown, ChevronUp } from 'lucide-react';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
  fallbackTitle?: string;
  fallbackMessage?: string;
  onReset?: () => void;
  showHomeButton?: boolean;
}

interface State {
  hasError: boolean;
  error?: Error;
  errorInfo?: ErrorInfo;
  copied: boolean;
  showDetails: boolean;
}

export class ErrorBoundary extends Component<Props, State> {
  public state: State = {
    hasError: false,
    copied: false,
    showDetails: false
  };

  public static getDerivedStateFromError(error: Error): Partial<State> {
    return { hasError: true, error };
  }

  public componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    if (process.env.NODE_ENV !== 'production') {
      console.error('[ErrorBoundary] Caught runtime error:', error, errorInfo);
    }
    this.setState({ errorInfo });
  }

  public handleReloadTable = () => {
    if (this.props.onReset) {
      this.setState({ hasError: false, error: undefined, errorInfo: undefined });
      this.props.onReset();
    } else {
      window.location.reload();
    }
  };

  public handleReturnToLobby = () => {
    // Clear room query if stuck in an invalid room state
    if (window.location.search.includes('room=')) {
      window.location.href = window.location.pathname;
    } else {
      window.location.reload();
    }
  };

  public handleCopyError = () => {
    const errorText = `${this.state.error?.name || 'Error'}: ${this.state.error?.message || 'Unknown'}\n\nStack:\n${this.state.error?.stack || ''}\n\nComponent Stack:\n${this.state.errorInfo?.componentStack || ''}`;
    navigator.clipboard?.writeText(errorText).then(() => {
      this.setState({ copied: true });
      setTimeout(() => this.setState({ copied: false }), 2000);
    }).catch(() => {});
  };

  public render() {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <div className="min-h-screen w-full bg-slate-950 text-slate-100 flex items-center justify-center p-4 relative overflow-hidden select-none">
          {/* Ambient casino table glow */}
          <div className="absolute inset-0 bg-radial from-emerald-950/40 via-slate-950/80 to-slate-950 pointer-events-none" />
          
          {/* Background decorative card suits */}
          <div className="absolute inset-0 flex justify-between items-center opacity-5 pointer-events-none text-9xl font-serif">
            <span className="ml-8 select-none">♠</span>
            <span className="text-red-500 select-none">♥</span>
            <span className="text-red-500 select-none">♦</span>
            <span className="mr-8 select-none">♣</span>
          </div>

          <div className="relative z-10 max-w-lg w-full bg-slate-900/90 border border-emerald-500/20 rounded-3xl p-6 sm:p-8 shadow-2xl shadow-emerald-950/50 backdrop-blur-xl animate-in fade-in zoom-in-95 duration-200">
            {/* Header Badge */}
            <div className="flex items-center justify-center mb-6">
              <div className="relative">
                <div className="w-20 h-20 rounded-2xl bg-gradient-to-br from-emerald-600/30 to-amber-500/20 border border-emerald-400/30 flex items-center justify-center shadow-lg shadow-emerald-900/40">
                  <span className="text-4xl">🃏</span>
                </div>
                <div className="absolute -bottom-2 -right-2 w-8 h-8 rounded-full bg-amber-500 text-slate-950 flex items-center justify-center shadow-md">
                  <AlertCircle className="w-5 h-5 stroke-[2.5]" />
                </div>
              </div>
            </div>

            {/* Title & Description */}
            <div className="text-center mb-8">
              <h2 className="text-2xl sm:text-3xl font-bold tracking-tight text-white mb-3">
                {this.props.fallbackTitle || "Masada Kısa Bir Mola"}
              </h2>
              <p className="text-slate-300 text-sm sm:text-base leading-relaxed">
                {this.props.fallbackMessage || "Beklenmeyen bir görsel hata oluştu. Oyun verileriniz ve masa bağlantınız korunuyor. Masayı yeniden yükleyerek hemen oyuna devam edebilirsiniz."}
              </p>
            </div>

            {/* Actions */}
            <div className="flex flex-col sm:flex-row gap-3 mb-6">
              <button
                type="button"
                onClick={this.handleReloadTable}
                className="flex-1 py-3.5 px-6 rounded-2xl bg-gradient-to-r from-emerald-600 to-teal-600 hover:from-emerald-500 hover:to-teal-500 active:scale-[0.98] text-white font-semibold text-base flex items-center justify-center gap-2.5 shadow-lg shadow-emerald-950/60 transition cursor-pointer border border-emerald-400/30"
              >
                <RotateCcw className="w-5 h-5" />
                Masayı Yeniden Yükle
              </button>

              <button
                type="button"
                onClick={this.handleReturnToLobby}
                className="py-3.5 px-5 rounded-2xl bg-slate-800/90 hover:bg-slate-700/90 active:scale-[0.98] text-slate-200 font-medium text-base flex items-center justify-center gap-2 border border-slate-700 transition cursor-pointer"
              >
                <Home className="w-5 h-5" />
                Lobiye Dön
              </button>
            </div>

            {/* Error Diagnostics Toggle */}
            {this.state.error && (
              <div className="pt-4 border-t border-slate-800">
                <button
                  type="button"
                  onClick={() => this.setState({ showDetails: !this.state.showDetails })}
                  className="w-full flex items-center justify-between text-xs text-slate-400 hover:text-slate-200 transition py-1"
                >
                  <span className="flex items-center gap-1.5 font-mono">
                    Hata Ayrıntısı ({this.state.error.name})
                  </span>
                  {this.state.showDetails ? (
                    <ChevronUp className="w-4 h-4" />
                  ) : (
                    <ChevronDown className="w-4 h-4" />
                  )}
                </button>

                {this.state.showDetails && (
                  <div className="mt-3 bg-slate-950/80 rounded-xl p-3 border border-slate-800/80 text-left">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-[11px] font-mono text-red-400 font-semibold truncate max-w-[280px]">
                        {this.state.error.message || 'Unknown error'}
                      </span>
                      <button
                        type="button"
                        onClick={this.handleCopyError}
                        className="text-[11px] text-slate-400 hover:text-white flex items-center gap-1 bg-slate-800/60 px-2 py-0.5 rounded cursor-pointer transition"
                      >
                        {this.state.copied ? (
                          <>
                            <Check className="w-3 h-3 text-emerald-400" />
                            Kopyalandı
                          </>
                        ) : (
                          <>
                            <Copy className="w-3 h-3" />
                            Kopyala
                          </>
                        )}
                      </button>
                    </div>
                    {this.state.error.stack && (
                      <pre className="text-[10px] text-slate-400 font-mono overflow-x-auto max-h-24 scrollbar-thin">
                        {this.state.error.stack.split('\n').slice(0, 5).join('\n')}
                      </pre>
                    )}
                  </div>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}
export default ErrorBoundary;
